import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';

import { fuseAnimations } from '@fuse/animations';
import { Subject, Observable, BehaviorSubject, merge } from 'rxjs';
import { DataSource } from '@angular/cdk/table';
import { takeUntil, tap } from 'rxjs/operators';
import { MatPaginator, MatSort, MatDialog, MatDialogRef, MatSnackBar } from '@angular/material';
import { RequestService } from '@fuse/services/request/request.service';
import { CompanyService } from '@fuse/services/company.service';
import { TranslateService } from '@ngx-translate/core';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

@Component({
    selector: 'request-list',
    templateUrl: './request-list.component.html',
    styleUrls: ['./request-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class RequestListComponent implements OnInit, AfterViewInit, OnDestroy {
    private _unsubscribeAll: Subject<any>;

    dataSource: FilesDataSource | null;
    displayedColumns = ['request', 'status', 'comment', 'pick_up_date', 'created_at'];
    requests: any;
    totalPages = 2;
    totalElements = 0;
    sortField = 'created_at';
    sordOrder = 'desc';
    pageSize = 10;
    lang = 'sr';
    company: any;

    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    constructor(
        private _requestService: RequestService,
        public _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _translate: TranslateService,
        private _translationLoader: FuseTranslationLoaderService,
        private _companyService: CompanyService
    ) {
        this._unsubscribeAll = new Subject();
    }

    private getTranslations(): void {
        this._translationLoader.getLastLang()
            .then(response => {
                this.lang = response['code'];
                this._translationLoader.getTranslations(this.lang, '23');
            })
            .catch(err => Promise.reject(err))
            .then(() => {
            }, err => {
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    loadRequestsPage(): void {
        console.log('REQUEST LOAD');
        this.dataSource.loadRequests(this.paginator.pageIndex + 1, this.paginator.pageSize, this.sort.active || 'created_at', this.sort.direction || 'desc', '', this.company.id);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this.dataSource = new FilesDataSource(this._requestService);
        this._companyService.getActiveCompany()
            .then(response => {
                this.company = response;
                console.log(this.company);
            })
            .catch(err => Promise.reject(err))
            .then(() => {
                this.dataSource.loadRequests(1, this.pageSize, this.sortField, this.sordOrder, '', this.company.id);
            }, err => {
            });

        this._requestService.onRequestsChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._companyService.getActiveCompany()
                    .then(response => {
                        this.company = response;
                    })
                    .catch(err => Promise.reject(err))
                    .then(() => {
                        this.loadRequestsPage();
                    }, err => {
                    });
            });

        this._translationLoader.onLangChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
                this.getTranslations();
            });

        this._companyService.onCompanyChanged
            .pipe(
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this._companyService.getActiveCompany()
                    .then(response => {
                        this.company = response;
                    })
                    .catch(err => Promise.reject(err))
                    .then(() => {
                        this.loadRequestsPage();
                    }, err => {
                    });
            });

        this.getTranslations();

    }

    ngAfterViewInit(): void {
        // reset the paginator after sorting
        this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

        // on sort or paginate events, load a new page
        merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                tap(() => this.loadRequestsPage())
            )
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe();
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}

// Custom Angular CDK Data Source
export class FilesDataSource extends DataSource<any>
{
    private requestsSubject = new BehaviorSubject<any[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false);

    public loading$ = this.loadingSubject.asObservable();

    /**
     * Constructor
     *
     * @param {RequestService} _requestService
     */
    constructor(
        private _requestService: RequestService
    ) {
        super();
    }

    connect(): Observable<any[]> {
        return this.requestsSubject.asObservable();
    }

    disconnect(): void {
        this.requestsSubject.complete();
        this.loadingSubject.complete();
    }

    setData(data): void {
        this.requestsSubject.next(data);
        this.loadingSubject.next(false);
    }

    loadRequests(page: number = 1, limit: number = 20, orderBy: string = 'created_at', order: string = 'desc', search: string = '', company: number = 1): void {
        this.loadingSubject.next(true);
        this._requestService.listSubmittedRequests(company, page, limit, orderBy, order, search).then(response => {
            this.requestsSubject.next(response.data);
            this.loadingSubject.next(false);
        });
    }
}
